import { BALL_RADIUS, PLAY_AREA_WIDTH, PLAY_AREA_HEIGHT } from './constants.js';

export const state = {
    // --- Game Mode and Progression ---
    currentPlayingMode: 'classic', // 'classic' or 'powerup'
    currentHole: 0, 
    currentHoleName: "", 
    totalScore: 0,  
    totalCoursePar: 0, 
    totalCourseDistance: 0, 
    totalCourseOptimalDistance: 0, 
    shotCount: 0, 
    currentPar: 0, 
    currentDynamicMaxDrag: 150, 
    currentHoleOptimalStartX: 0,
    currentHoleOptimalStartY: 0, 
    currentHoleOptimalDistance: 0,
    TOTAL_HOLES: 9,

    // --- Zoom and View Coordinates ---
    isButtonZoomActive: false, 
    isPinching: false,
    initialPinchDistance: 0,
    pinchStartScale: 1.0,
    isInteractingZoom: false, 
    lastScrollEventTime: 0, 
    zoomInteractionEndTime: 0, 
    initialCourseViewActive: false,
    initialCourseViewStartTime: 0,
    
    viewTargetScale: 1.0,
    viewCurrentScale: 1.0,
    viewTargetCenterX: PLAY_AREA_WIDTH / 2,
    viewTargetCenterY: PLAY_AREA_HEIGHT / 2,
    viewCurrentCenterX: PLAY_AREA_WIDTH / 2,
    viewCurrentCenterY: PLAY_AREA_HEIGHT / 2,
    MIN_PLAY_AREA_VIEW_SCALE: 0.2,

    // --- Gameplay Engine State ---
    gameState: {
        balls: [], 
        planets: [], 
        goal: { planetIndex: 0, angleOnPlanet: Math.PI / 4, holeRadius: 14, flagHeight: 35 }, 
        isAiming: false,
        aimAngle: 0,
                aimPower: 0, 
        goalReached: false, 
        previousShotTrail: [], 
        lastShotDistanceTraveled: 0, 
        currentHoleDistanceTraveled: 0, 
        multiBallActiveOnScreen: false,
        coins: []
    },
    isArcadeRunOver: false, 

    // --- Tee Flight State ---
    teeState: {
        visible: false,
        tipX: 0, tipY: 0,   
        angle: 0,           
        height: BALL_RADIUS * 0.3, 
        width: BALL_RADIUS * 0.5, 
        flying: false,
        fx: 0, fy: 0,       
        fvx: 0, fvy: 0,     
        fAngle: 0,          
        fRotationSpeed: 0,
        fLife: 0            
    },

    // --- Space Bucks & Power-Ups ---
    totalSpaceBucks: 0,
    ownedPowerUps: {
        trajectoryLengthener: 0,
        gravityGrid: 0,
        stickyBall: 0,
        linearLaser: 0,
        chronoRewind: 0,
        multiBall: 0,
        oobShield: 0
    },
    activePowerUps: {
        trajectoryLengthener: false,
        gravityGrid: false,
        stickyBall: false,
        linearLaser: false,
        chronoRewind: false,
        multiBall: false,
        oobShield: false
    },
    rewindState: null,
    boundaryShieldImpactTime: 0,
    // --- Elements Lists ---
    stars: [],
    courseHighScores: {}, 
    fireworks: [],
    collisionParticles: [],
    wormholeActive: false,
    wormholeProgress: 0,
    savedCourses: [], 
    currentEditingCourse: null, 
    isPlayingCustomCourse: false,
    currentPlayingCourseDefinition: null, 
    currentCustomCourseName: "", 
    currentClassicCourseLevelsPlayed: [], 
    currentPlayerName: "Player",
    customBallColor: "#FFFFFF",
    customBallSkin: "golf", // 'golf', 'rave', 'cat', 'neon'
    customBallTrail: "neon", // 'neon', 'rainbow', 'stardust'
    savedLevels: [],

    // --- Sound and Loops ---
    IS_MUTED: false,
    IS_MUSIC_MUTED: false,
    IS_CRT_ACTIVE: false,
    openedShopFromGoalScreen: false,
    gameLoopRequestId: null,
    lastRunTimestamp: null,
    spectatingPlayerId: null,
    localFinishCelebrationEndTime: 0,
    lastDeltaTime: 1 / 60
};

// --- Storage State Synchronization Helpers ---
export function saveGameData() { 
    if (state.currentPlayingMode === 'powerup') { 
        localStorage.setItem('totalSpaceBucks', state.totalSpaceBucks.toString());
        localStorage.setItem('ownedPowerUps', JSON.stringify(state.ownedPowerUps));
    }
    localStorage.setItem('spaceGolfCustomColor', state.customBallColor);
    localStorage.setItem('spaceGolfCustomSkin', state.customBallSkin);
    localStorage.setItem('spaceGolfCustomTrail', state.customBallTrail);
}

export function loadGameData() {
    const savedMuted = localStorage.getItem('isMuted');
    state.IS_MUTED = savedMuted === 'true';
    
    const savedMusicMuted = localStorage.getItem('isMusicMuted');
    state.IS_MUSIC_MUTED = savedMusicMuted === 'true';
    
    const savedCRTActive = localStorage.getItem('isCRTActive');
    state.IS_CRT_ACTIVE = savedCRTActive === 'true';
    
    state.currentPlayerName = localStorage.getItem('spaceGolfPlayerName') || "Player";
    state.customBallColor = localStorage.getItem('spaceGolfCustomColor') || "#FFFFFF";
    state.customBallSkin = localStorage.getItem('spaceGolfCustomSkin') || "golf";
    state.customBallTrail = localStorage.getItem('spaceGolfCustomTrail') || "neon";

    const storedHighScores = localStorage.getItem('spaceGolfHighScores');
    if (storedHighScores) {
        try {
            state.courseHighScores = JSON.parse(storedHighScores);
        } catch (e) {
            console.error("Error parsing stored high scores: ", e);
            state.courseHighScores = {};
        }
    } else { 
        state.courseHighScores = {}; 
    }

    const storedCourses = localStorage.getItem('spaceGolfSavedCourses');
    if (storedCourses) {
        try {
            state.savedCourses = JSON.parse(storedCourses);
        } catch(e) {
            console.error("Error parsing saved courses: ", e);
            state.savedCourses = [];
        }
    } else { 
        state.savedCourses = []; 
    }

    // --- Space Bucks & Power-Ups ---
    const storedSpaceBucks = localStorage.getItem('totalSpaceBucks');
    if (storedSpaceBucks) {
        state.totalSpaceBucks = parseInt(storedSpaceBucks);
    } else {
        state.totalSpaceBucks = 0;
    }
    
    const storedPowerUps = localStorage.getItem('ownedPowerUps');
    const defaultPowerUps = {
        trajectoryLengthener: 0,
        gravityGrid: 0,
        stickyBall: 0,
        linearLaser: 0,
        chronoRewind: 0,
        multiBall: 0,
        oobShield: 0
    };
    if (storedPowerUps) {
        try {
            const parsed = JSON.parse(storedPowerUps);
            state.ownedPowerUps = { ...defaultPowerUps, ...parsed };
        } catch(e) {
            console.error("Error parsing stored powerups: ", e);
            state.ownedPowerUps = { ...defaultPowerUps };
        }
    } else {
        state.ownedPowerUps = { ...defaultPowerUps };
    }
}
